import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  CreditCardOutlined,
  ExclamationCircleFilled,
  InfoCircleFilled,
  LogoutOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { Button, Modal, Popconfirm, Skeleton } from 'antd';
import { setLogoutAction } from 'common/commonRedux/auth/actions';
import { IConfirmModal } from 'common/components';
import { useAxiosRequest, useIsDark } from 'common/hooks';
import SelectPlanModal from 'modules/AccountManagement/Billing/Subscriptions/SelectPlanModal';
import BillingModal from 'modules/auth/signUp/BillingModal';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

// Mirrors AccountStatusSummaryDTO.UiState on the API.
//
// `RenewalDue` and `GraceExpiring` replaced the old, overloaded `GracePeriod`, which the API
// returned for two opposite situations (paid period ending soon vs. paid period already
// lapsed). `TrialGrace` is new: a forced trial has ended, the first invoice has been issued,
// and the org has a grace window to pay before being blocked.
type UiState =
  | 'Active'
  | 'Trial'
  | 'TrialExpired'
  | 'TrialGrace'
  | 'RenewalDue'
  | 'GraceExpiring'
  | 'InvoicePending'
  | 'PlanRequired'
  | 'Suspended'
  | string;

type BillingPeriod = 'Monthly' | 'Yearly';

interface PlanPricing {
  billingPeriod: BillingPeriod;
  pricePerUser: number;
  discountPercent: number;
  effectivePricePerUser: number;
  currencyCode: string;
  currencySymbol: string;
  isDiscountActive: boolean;
  discountValidFrom: string | null;
  discountValidTo: string | null;
  isDiscountForever: boolean;
}

interface Plan {
  planId: number;
  planName: string;
  description: string;
  planType: string;
  isSelfServe: boolean;
  sortOrder: number;
  pricing: PlanPricing[];
  features: string[];
}

interface PublicPlansResponse {
  plans: Plan[];
}

interface CurrentSubscription {
  planName: string;
  planType: string;
  employeesUsed: number;
  employeeSlots: number;
  billingPeriod: BillingPeriod;
  effectivePrice: number;
  totalMonthlyAmount: number;
}

interface PreviewData {
  amount: number;
  billType: string;
  proRateDays?: number;
  totalDaysInPeriod?: number;
  fromDate?: string;
  toDate?: string;
  description?: string;
}

interface UnpaidBill {
  billId: number;
  amount: number;
  billType: string;
  fromDate: string | null;
  toDate: string | null;
  payableDate: string | null;
  billStatus: string;
  isOverdue: boolean;
  daysOverdue: number;
  planName: string | null;
}

interface AccountStatus {
  uiState: UiState;
  isTrialActive: boolean;
  trialDaysRemaining: number | null;
  trialHoursRemaining: number | null;
  trialEndDate: string | null;
  hasDueBill: boolean;
  dueBillId: number | null;
  dueBillAmount: number | null;
  billDueDate: string | null;
  unpaidBills?: UnpaidBill[];
  totalUnpaidCount?: number;
  totalAmountDue?: number;
  isInGracePeriod: boolean;
  graceHoursRemaining: number;
  graceEndDate: string | null;
  isSuspended: boolean;
  canAccess: boolean;
  accountStatus: string;
  planName: string | null;
  message: string;
  isFree: boolean;
  isAdminAssigned: boolean;
}

const POLL_MS = 30 * 60 * 1000; // 30 minutes
const SNOOZE_MS = 60 * 60 * 1000; // 1 hour
const SNOOZE_KEY = 'accountStatusGate_snoozedUntil';

const getSnoozeRemainingMs = (): number => {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    if (!raw) return 0;
    const until = parseInt(raw, 10);
    if (!Number.isFinite(until)) return 0;
    const remaining = until - Date.now();
    if (remaining <= 0) {
      localStorage.removeItem(SNOOZE_KEY);
      return 0;
    }
    return remaining;
  } catch {
    return 0;
  }
};

const setSnooze = () => {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
  } catch {
    // ignore quota/availability errors
  }
};

const clearSnooze = () => {
  try {
    localStorage.removeItem(SNOOZE_KEY);
  } catch {
    // ignore
  }
};

const formatDate = (iso: string | null) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const formatPrice = (v: number | null, sym = '$') =>
  v == null || !Number.isFinite(v) ? '' : `${sym}${v.toFixed(2)}`;

type GateMode =
  | { kind: 'none' }
  | { kind: 'trial-reminder' }
  | { kind: 'trial-expired' }
  | { kind: 'bill-due' }
  | { kind: 'grace-ongoing' }
  | { kind: 'grace-expired' }
  | { kind: 'suspended' };

const decide = (s: AccountStatus | null): GateMode => {
  if (!s) return { kind: 'none' };
  if (!s.isTrialActive && s.isFree) return { kind: 'none' };
  // Grace ran out — block until user resolves billing.
  if ((s.isInGracePeriod && (s.graceHoursRemaining ?? 0) <= 0) || s.uiState === "PlanRequired") {
    return { kind: 'grace-expired' };
  }
  if (s.isSuspended) return { kind: 'suspended' };

  // Forced trial has ended, the first invoice is issued and the grace window is running.
  // Reuses the grace-ongoing screen, which already renders the unpaid-bills list with a
  // Pay Now action when bills are present — exactly what is needed here. Dismissible: the
  // whole point of the grace window is that access continues while the invoice is settled.
  if (s.uiState === 'TrialGrace' && (s.graceHoursRemaining ?? 0) > 0) {
    return { kind: 'grace-ongoing' };
  }

  // Still inside grace window — soft reminder.
  // Covers RenewalDue (paid period ending soon) and GraceExpiring (paid period lapsed,
  // renewal grace counting down); both set isInGracePeriod on the API side.
  if (s.isInGracePeriod) return { kind: 'grace-ongoing' };

  // Trial fully exhausted (no days and no hours left) — block.
  const trialDays = s.trialDaysRemaining ?? 0;
  const trialHours = s.trialHoursRemaining ?? 0;
  if (s.isTrialActive && trialDays <= 0 && trialHours <= 0) {
    return { kind: 'trial-expired' };
  }
  // Server-flagged trial expiry (fallback).
  if (s.uiState === 'TrialExpired' || s.accountStatus === 'Pending') {
    return { kind: 'trial-expired' };
  }

  // Trial still valid but a bill is pending — soft "pay now or later".
  if (s.hasDueBill && s.dueBillId) return { kind: 'bill-due' };

  if (s.isTrialActive || s.uiState === 'Trial') return { kind: 'trial-reminder' };
  return { kind: 'none' };
};

const isBlocking = (mode: GateMode) =>
  mode.kind === 'trial-expired' ||
  mode.kind === 'grace-expired' ||
  mode.kind === 'suspended';

const AccountStatusGate: FC = () => {
  const { t } = useTranslation();
  const isDark = useIsDark();
  const { isAuth } = useSelector(
    (state: any) => state?.auth ?? {},
    shallowEqual
  );
  const dispatch: any = useDispatch();

  const [status, setStatus] = useState<AccountStatus | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [payOpen, setPayOpen] = useState<boolean>(false);
  const [payTarget, setPayTarget] = useState<{ billId: number; amount: number } | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [view, setView] = useState<'main' | 'picker'>('main');
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('Monthly');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentSub, setCurrentSub] = useState<CurrentSubscription | null>(null);
  const [selectPlanOpen, setSelectPlanOpen] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [proPreview, setProPreview] = useState<PreviewData | null>(null);
  const [currentPreview, setCurrentPreview] = useState<PreviewData | null>(
    null
  );

  const statusReq = useAxiosRequest(null);
  const cancelReq = useAxiosRequest(null);
  const plansReq = useAxiosRequest(null);
  const currentSubReq = useAxiosRequest(null);
  const renewReq = useAxiosRequest(null);
  const proPreviewReq = useAxiosRequest(null);
  const currentPreviewReq = useAxiosRequest(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const snoozeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchStatus = useCallback(
    (autoOpen: boolean) => {
      statusReq.apiAction({
        urlObjKey: 'GetAccountStatus',
        method: 'GET',
        cb: (data: any) => {
          const next = data as AccountStatus;
          setStatus(next);
          const nextMode = decide(next);
          if (nextMode.kind === 'none') {
            setOpen(false);
            clearSnooze();
            return;
          }
          if (autoOpen) {
            // Suppress auto-open for dismissible reminders during snooze.
            if (!isBlocking(nextMode) && getSnoozeRemainingMs() > 0) return;
            setOpen(true);
          }
        },
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    []
  );

  // Fetch on auth + every 30 minutes; clean up on logout / unmount.
  useEffect(() => {
    if (!isAuth) {
      setStatus(null);
      setOpen(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (snoozeTimeoutRef.current) {
        clearTimeout(snoozeTimeoutRef.current);
        snoozeTimeoutRef.current = null;
      }
      return;
    }
    fetchStatus(true);
    intervalRef.current = setInterval(() => fetchStatus(true), POLL_MS);
    // If a snooze is in effect (e.g. user reloaded mid-snooze), schedule a
    // re-check the moment it expires so the modal re-appears without a reload.
    const remaining = getSnoozeRemainingMs();
    if (remaining > 0) {
      snoozeTimeoutRef.current = setTimeout(() => {
        snoozeTimeoutRef.current = null;
        fetchStatus(true);
      }, remaining);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (snoozeTimeoutRef.current) {
        clearTimeout(snoozeTimeoutRef.current);
        snoozeTimeoutRef.current = null;
      }
    };
  }, [isAuth, fetchStatus]);

  const mode = decide(status);
  const blocking = isBlocking(mode);

  // For grace states with no outstanding bill, the user can renew their
  // existing plan in-place. Eagerly fetch the bits we need so the CTA can
  // fire without an extra click-and-wait.
  useEffect(() => {
    const noBills = !(status?.unpaidBills?.length);
    const inRenewable =
      mode.kind === 'grace-ongoing' || mode.kind === 'grace-expired';
    if (!inRenewable || !noBills) return;
    if (!currentSub && !currentSubReq.isLoading) fetchCurrentSub();
    if (!plans.length && !plansReq.isLoading) fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode.kind, status?.unpaidBills?.length]);

  // Once currentSub + plans are loaded in a renew state, preview both the
  // Professional bill (recommended) and the current-plan bill so the user
  // sees the actual amount upfront.
  useEffect(() => {
    if (!currentSub || !plans.length) return;
    const noBills = !(status?.unpaidBills?.length);
    const inRenewable =
      mode.kind === 'grace-ongoing' || mode.kind === 'grace-expired';
    if (!inRenewable || !noBills) return;

    const proPlan = plans.find(
      (p) =>
        (p.planType || '').toLowerCase() === 'professional' ||
        (p.planName || '').toLowerCase() === 'professional'
    );
    const currentPlan = plans.find((p) => p.planType === currentSub.planType);
    const isOnPro =
      !!currentPlan &&
      ((currentPlan.planType || '').toLowerCase() === 'professional' ||
        (currentPlan.planName || '').toLowerCase() === 'professional');

    if (proPlan && !isOnPro && !proPreview && !proPreviewReq.isLoading) {
      proPreviewReq.apiAction({
        urlObjKey: 'PreviewSelectPlan',
        method: 'POST',
        payload: {
          planId: proPlan.planId,
          billingPeriod: currentSub.billingPeriod,
          employeeSlots: currentSub.employeeSlots,
        },
        cb: (data: any) => setProPreview(data as PreviewData),
      });
    }
    if (
      currentPlan &&
      !currentPreview &&
      !currentPreviewReq.isLoading
    ) {
      currentPreviewReq.apiAction({
        urlObjKey: 'PreviewSelectPlan',
        method: 'POST',
        payload: {
          planId: currentPlan.planId,
          billingPeriod: currentSub.billingPeriod,
          employeeSlots: currentSub.employeeSlots,
        },
        cb: (data: any) => setCurrentPreview(data as PreviewData),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSub, plans.length, mode.kind, status?.unpaidBills?.length]);

  const fetchPlans = useCallback(() => {
    plansReq.apiAction({
      urlObjKey: 'GetPublicPricePlans',
      method: 'GET',
      cb: (data: any) => {
        const list = Array.isArray((data as PublicPlansResponse)?.plans)
          ? (data as PublicPlansResponse).plans
          : [];
        setPlans(list);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCurrentSub = useCallback(() => {
    currentSubReq.apiAction({
      urlObjKey: 'GetCurrentSubscription',
      method: 'GET',
      cb: (data: any) => {
        const sub = data as CurrentSubscription | null;
        if (sub) {
          setCurrentSub(sub);
          if (sub.billingPeriod) setBillingPeriod(sub.billingPeriod);
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enterPicker = () => {
    setView('picker');
    if (!plans.length) fetchPlans();
    if (!currentSub) fetchCurrentSub();
  };

  const createBillForPlan = (plan: Plan) => {
    if (!currentSub) return;
    renewReq.apiAction({
      urlObjKey: 'SelectPlan',
      method: 'POST',
      payload: {
        planId: plan.planId,
        billingPeriod: currentSub.billingPeriod,
        employeeSlots: currentSub.employeeSlots,
        selectedEmployeeIds: [],
      },
      isToast: true,
      successMessage: t('manageAccount:accountStatusGate.billsList.successCreate'),
      cb: (data: any) => {
        const bill = data;
        if (bill?.billId) {
          setPayTarget({
            billId: bill.billId,
            amount: bill.billAmount ?? bill.amount ?? 0,
          });
          setOpen(false);
          setPayOpen(true);
        } else {
          fetchStatus(true);
        }
      },
    });
  };

  const handleRenewCurrentPlan = () => {
    if (!currentSub) {
      fetchCurrentSub();
      return;
    }
    if (!plans.length) {
      fetchPlans();
      return;
    }
    const currentPlan = plans.find((p) => p.planType === currentSub.planType);
    if (!currentPlan) return;
    createBillForPlan(currentPlan);
  };

  const findProfessionalPlan = (): Plan | null =>
    plans.find(
      (p) =>
        (p.planType || '').toLowerCase() === 'professional' ||
        (p.planName || '').toLowerCase() === 'professional'
    ) || null;

  const handleSelectRecommendedPlan = () => {
    if (!currentSub) {
      fetchCurrentSub();
      return;
    }
    if (!plans.length) {
      fetchPlans();
      return;
    }
    const proPlan = findProfessionalPlan();
    if (!proPlan) return;
    createBillForPlan(proPlan);
  };

  const exitPicker = () => setView('main');

  const handlePlanSelect = (plan: Plan) => {
    setSelectedPlan(plan);
    setSelectPlanOpen(true);
  };

  const handleSelectPlanClose = () => {
    setSelectPlanOpen(false);
    setSelectedPlan(null);
  };

  const handleSelectPlanCompleted = () => {
    setView('main');
    fetchStatus(true);
  };

  const openPayment = (billId?: number, amount?: number) => {
    const targetId = billId ?? status?.dueBillId ?? null;
    const targetAmount = amount ?? status?.dueBillAmount ?? 0;
    if (!targetId) return;
    setPayTarget({ billId: targetId, amount: targetAmount });
    setOpen(false);
    setPayOpen(true);
  };

  const handlePayClose = (next: boolean) => {
    setPayOpen(next);
    if (!next) {
      setPayTarget(null);
      // Re-check status after payment flow closes
      fetchStatus(true);
    }
  };

  const handleCancelBill = (billId: number) => {
    setCancellingId(billId);
    cancelReq.apiAction({
      urlObjKey: 'CancelPendingBill',
      method: 'DELETE',
      paramId: billId,
      isToast: true,
      successMessage: t('manageAccount:accountStatusGate.billsList.successCancel'),
      cb: () => {
        setCancellingId(null);
        setView('main');
        fetchStatus(true);
      },
      onError: () => setCancellingId(null),
    });
  };

  const handleLogout = () => {
    IConfirmModal({
      title: t('layout:logoutPopup.confirmTitle'),
      message: t('layout:logoutPopup.confirmMessage'),
      yesAlertFunc() {
        dispatch(setLogoutAction());
      },
      noAlertFunc() {},
      buttonText: t('dashboard:logOutProfile:logout'),
    });
  };

  const handleDismiss = () => {
    if (blocking) return;
    setSnooze();
    setOpen(false);
    if (snoozeTimeoutRef.current) clearTimeout(snoozeTimeoutRef.current);
    snoozeTimeoutRef.current = setTimeout(() => {
      snoozeTimeoutRef.current = null;
      fetchStatus(true);
    }, SNOOZE_MS);
  };

  const sortedPlans = useMemo<Plan[]>(() => {
    if (!plans.length) return [];
    return [...plans].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [plans]);

  if (!isAuth || mode.kind === 'none' || !status) {
    return payOpen && payTarget ? (
      <BillingModal
        billingId={String(payTarget.billId)}
        ammount={payTarget.amount}
        open={payOpen}
        setOpen={handlePayClose}
      />
    ) : null;
  }

  const unpaidBills: UnpaidBill[] = Array.isArray(status.unpaidBills)
    ? status.unpaidBills
    : [];
  const hasUnpaidBills = unpaidBills.length > 0;

  const renderUnpaidBillsList = () => {
    const totalDue = unpaidBills.reduce((sum, b) => sum + (b.amount || 0), 0);
    return (
      <div className="space-y-3">
        {unpaidBills.length > 1 && (
          <div className="flex items-center justify-between px-1">
            <span className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
              {t('manageAccount:accountStatusGate.billsList.outstandingBills', { count: unpaidBills.length })}
            </span>
            <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-[#101828]'}`}>
              {t('manageAccount:accountStatusGate.billsList.totalLabel', { amount: formatPrice(totalDue) })}
            </span>
          </div>
        )}

        <div className="space-y-2.5">
          {unpaidBills.map((bill) => {
            const cancelling =
              cancellingId === bill.billId && cancelReq.isLoading;
            const accent = bill.isOverdue
              ? isDark
                ? 'border-red-900/40 bg-red-900/15'
                : 'border-red-200 bg-red-50/40'
              : isDark
                ? 'border-[#2E3138] bg-[#0F1E33]'
                : 'border-[#EAECF0] bg-white';
            return (
              <div
                key={bill.billId}
                className={`border ${accent} rounded-xl p-4 transition-shadow hover:shadow-sm`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-[#101828]'}`}>
                        {bill.planName || bill.billType || t('manageAccount:accountStatusGate.billsList.fallbackName')}
                      </span>
                      <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${isDark ? 'text-[#94A3B8] bg-[#1E293B]' : 'text-[#475467] bg-[#F2F4F7]'}`}>
                        {t('manageAccount:accountStatusGate.billsList.billIdPrefix', { billId: bill.billId })}
                      </span>
                      {bill.isOverdue && bill.daysOverdue > 0 && (
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${isDark ? 'text-red-300 bg-red-900/30' : 'text-red-700 bg-red-100'}`}>
                          {t('manageAccount:accountStatusGate.billsList.overdueWithDays', { days: bill.daysOverdue })}
                        </span>
                      )}
                    </div>
                    <div className={`text-xs mt-1 ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                      {t('manageAccount:accountStatusGate.billsList.due', { date: formatDate(bill.payableDate) })}
                      {bill.fromDate && bill.toDate && (
                        <span className={isDark ? 'text-[#64748B]' : 'text-[#98A2B3]'}>
                          {' · '}
                          {t('manageAccount:accountStatusGate.billsList.dateRange', { from: formatDate(bill.fromDate), to: formatDate(bill.toDate) })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-xl font-bold leading-tight ${isDark ? 'text-white' : 'text-[#101828]'}`}>
                      {formatPrice(bill.amount)}
                    </div>
                  </div>
                </div>
                <div className={`flex justify-end gap-2 pt-2 border-t ${isDark ? 'border-[#2E3138]' : 'border-[#F2F4F7]'}`}>
                  {
                    !status.isAdminAssigned && (
                      <Popconfirm
                        title={t('manageAccount:accountStatusGate.billsList.popconfirmTitle')}
                        description={t('manageAccount:accountStatusGate.billsList.popconfirmDesc')}
                        okText={t('manageAccount:accountStatusGate.billsList.popconfirmOk')}
                        cancelText={t('manageAccount:accountStatusGate.billsList.popconfirmCancel')}
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleCancelBill(bill.billId)}
                      >
                        <Button size="small" danger ghost loading={cancelling}>
                          {t('manageAccount:accountStatusGate.buttons.cancel')}
                        </Button>
                      </Popconfirm>
                    )
                  }

                  <Button
                    size="small"
                    type="primary"
                    className="bg-primaryColor"
                    onClick={() => openPayment(bill.billId, bill.amount)}
                  >
                    {t('manageAccount:accountStatusGate.buttons.payNow')}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPicker = () => {
    const popularIdx =
      sortedPlans.length >= 3 ? 1 : sortedPlans.length === 2 ? 1 : -1;

    return (
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div>
            <div className={`text-base font-semibold ${isDark ? 'text-white' : 'text-[#101828]'}`}>
              {t('manageAccount:accountStatusGate.picker.title')}
            </div>
            <div className={`text-xs mt-0.5 ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
              {t('manageAccount:accountStatusGate.picker.subtitle')}
            </div>
          </div>
          <div className={`inline-flex rounded-full p-1 shrink-0 self-start sm:self-auto ${isDark ? 'bg-[#1E293B]' : 'bg-[#F2F4F7]'}`}>
            {(['Monthly', 'Yearly'] as BillingPeriod[]).map((bp) => (
              <button
                key={bp}
                type="button"
                onClick={() => setBillingPeriod(bp)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${billingPeriod === bp
                  ? isDark
                    ? 'bg-[#0B172B] text-primaryColor shadow-sm'
                    : 'bg-white text-primaryColor shadow-sm'
                  : isDark
                    ? 'text-[#94A3B8] hover:text-white'
                    : 'text-[#475467] hover:text-[#101828]'
                  }`}
              >
                {bp}
                {bp === 'Yearly' && (
                  <span className={`ml-1 text-[9px] font-extrabold animate-pulse rotate-2 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    {t('manageAccount:accountStatusGate.picker.save')}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {plansReq.isLoading && !sortedPlans.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[0, 1].map((i) => (
              <div
                key={i}
                className={`border rounded-xl p-4 ${isDark ? 'border-[#2E3138] bg-[#0B172B]' : 'border-[#EAECF0] bg-white'}`}
              >
                <Skeleton active paragraph={{ rows: 4 }} />
              </div>
            ))}
          </div>
        ) : sortedPlans.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-stretch">
            {sortedPlans.map((plan, idx) => {
              const pricing =
                plan.pricing?.find((p) => p.billingPeriod === billingPeriod) ||
                plan.pricing?.[0];
              const isCustom = !plan.isSelfServe;
              const isPopular = idx === popularIdx && !isCustom;

              if (isCustom) {
                return (
                  <div
                    key={plan.planId}
                    className={`sm:col-span-2 relative rounded-xl p-5 border ${isDark ? 'bg-[#0F1E33] border-[#2E3138]' : 'bg-gray-100 border-gray-200'}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-start gap-4 min-w-0 flex-1">
                        <div className={`w-11 h-11 rounded-full flex items-center justify-center text-xl shrink-0 ${isDark ? 'bg-white/5' : 'bg-white/10'}`}>
                          <CheckCircleFilled className={isDark ? 'text-emerald-300' : 'text-emerald-400'} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-base font-semibold ${isDark ? 'text-white' : 'text-[#101828]'}`}>
                              {plan.planName}
                            </span>
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${isDark ? 'bg-[#1E293B] text-[#CBD5E1]' : 'bg-white text-[#101828]'}`}>
                              {t('manageAccount:accountStatusGate.picker.custom')}
                            </span>
                          </div>
                          <div className={`text-sm mt-1 leading-relaxed ${isDark ? 'text-[#CBD5E1]' : 'text-[#475467]'}`}>
                            {plan.description ||
                              t('manageAccount:accountStatusGate.picker.customFallback')}
                          </div>
                          {plan.features?.length ? (
                            <ul className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
                              {plan.features.map((f) => (
                                <li
                                  key={f}
                                  className={`flex items-center gap-1.5 text-xs ${isDark ? 'text-[#CBD5E1]' : 'text-[#344054]'}`}
                                >
                                  <CheckCircleFilled className={`text-[11px] ${isDark ? 'text-emerald-300' : 'text-emerald-400'}`} />
                                  <span>{f}</span>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      </div>
                      <Button
                        size="large"
                        onClick={() =>
                          window.open('https://trackforce.io/contact/', '_blank')
                        }
                        className={`hover:!opacity-90 !border-0 !font-semibold shrink-0 ${isDark ? '!text-white !bg-[#1E293B]' : '!text-[#0F172A]'}`}
                      >
                        {t('manageAccount:accountStatusGate.buttons.contactSales')}
                      </Button>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={plan.planId}
                  className={`relative flex flex-col rounded-xl p-5 transition-all ${isPopular
                    ? isDark
                      ? 'bg-[#0B172B] border-2 border-primaryColor shadow-md'
                      : 'bg-white border-2 border-primaryColor shadow-md'
                    : isDark
                      ? 'bg-[#0B172B] border border-[#2E3138] hover:border-[#334155] hover:shadow-sm'
                      : 'bg-white border border-[#EAECF0] hover:border-[#D0D5DD] hover:shadow-sm'
                    }`}
                >
                  {isPopular && (
                    <div className="absolute -top-2.5 right-4 bg-primaryColor text-white text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full shadow">
                      {t('manageAccount:accountStatusGate.picker.mostPopular')}
                    </div>
                  )}

                  <div className="mb-3">
                    <div className={`text-base font-semibold ${isDark ? 'text-white' : 'text-[#101828]'}`}>
                      {plan.planName}
                    </div>
                    <div className={`text-xs leading-snug mt-1 min-h-[32px] ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                      {plan.description}
                    </div>
                  </div>

                  <div className="mb-4 min-h-[56px]">
                    {pricing ? (
                      <>
                        <div className="flex items-baseline gap-1">
                          <span className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-[#101828]'}`}>
                            {pricing.currencySymbol}
                            {pricing.effectivePricePerUser.toFixed(2)}
                          </span>
                          <span className={`text-xs ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                            {t('manageAccount:accountStatusGate.picker.perUserMo')}
                          </span>
                        </div>
                        {pricing.isDiscountActive &&
                          pricing.discountPercent > 0 ? (
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className={`text-xs line-through ${isDark ? 'text-[#64748B]' : 'text-gray-400'}`}>
                              {pricing.currencySymbol}
                              {pricing.pricePerUser.toFixed(2)}
                            </span>
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${isDark ? 'text-emerald-300 bg-emerald-900/30' : 'text-emerald-700 bg-emerald-50'}`}>
                              {t('manageAccount:accountStatusGate.picker.saveDiscount', { percent: pricing.discountPercent })}
                            </span>
                          </div>
                        ) : (
                          <div className={`text-xs mt-1 ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                            {billingPeriod === 'Yearly'
                              ? t('manageAccount:accountStatusGate.picker.billedYearly')
                              : t('manageAccount:accountStatusGate.picker.billedMonthly')}
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>

                  {plan.features?.length ? (
                    <ul className="space-y-2 mb-4 flex-1 max-h-[150px] overflow-y-auto custom-scrollbar pr-1">
                      {plan.features.map((f) => (
                        <li
                          key={f}
                          className={`flex items-start gap-2 text-xs ${isDark ? 'text-[#CBD5E1]' : 'text-[#344054]'}`}
                        >
                          <CheckCircleFilled className={`mt-0.5 text-[12px] flex-shrink-0 ${isDark ? 'text-emerald-400' : 'text-emerald-500'}`} />
                          <span className="leading-snug">{f}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="flex-1" />
                  )}

                  <Button
                    block
                    type="primary"
                    className="bg-primaryColor"
                    onClick={() => handlePlanSelect(plan)}
                  >
                    {t('manageAccount:accountStatusGate.buttons.selectPlan')}
                  </Button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={`py-10 text-center text-sm border border-dashed rounded-xl ${isDark ? 'text-[#94A3B8] border-[#2E3138]' : 'text-[#475467] border-[#EAECF0]'}`}>
            {t('manageAccount:accountStatusGate.picker.noPlans')}
          </div>
        )}
      </div>
    );
  };

  const Hero = ({
    icon,
    tone,
    title,
    subtitle,
  }: {
    icon: React.ReactNode;
    tone: 'blue' | 'amber' | 'red' | 'emerald';
    title: string;
    subtitle?: string;
  }) => {
    const toneClassesLight = {
      blue: { bg: 'bg-blue-50', ring: 'ring-blue-100', text: 'text-blue-600' },
      amber: { bg: 'bg-amber-50', ring: 'ring-amber-100', text: 'text-amber-600' },
      red: { bg: 'bg-red-50', ring: 'ring-red-100', text: 'text-red-600' },
      emerald: { bg: 'bg-emerald-50', ring: 'ring-emerald-100', text: 'text-emerald-600' },
    }[tone];
    const toneClassesDark = {
      blue: { bg: 'bg-blue-900/25', ring: 'ring-blue-900/30', text: 'text-blue-300' },
      amber: { bg: 'bg-amber-900/25', ring: 'ring-amber-900/30', text: 'text-amber-300' },
      red: { bg: 'bg-red-900/25', ring: 'ring-red-900/30', text: 'text-red-300' },
      emerald: { bg: 'bg-emerald-900/25', ring: 'ring-emerald-900/30', text: 'text-emerald-300' },
    }[tone];
    const toneClasses = isDark ? toneClassesDark : toneClassesLight;
    return (
      <div className="flex items-start gap-3.5 mb-4">
        <div
          className={`w-11 h-11 rounded-full flex items-center justify-center text-xl ${toneClasses.bg} ${toneClasses.text} ring-4 ring-inset ${toneClasses.ring} shrink-0`}
        >
          {icon}
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <div className={`text-base font-semibold leading-snug ${isDark ? 'text-white' : 'text-[#101828]'}`}>
            {title}
          </div>
          {subtitle && (
            <div className={`text-sm mt-1 leading-relaxed ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
              {subtitle}
            </div>
          )}
        </div>
      </div>
    );
  };

  const StatRow = ({
    label,
    value,
    valueClassName,
  }: {
    label: string;
    value: React.ReactNode;
    valueClassName?: string;
  }) => {
    const defaultValueClass = isDark ? 'text-white' : 'text-[#101828]';
    return (
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className={isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}>{label}</span>
        <span className={`font-semibold ${valueClassName ?? defaultValueClass}`}>{value}</span>
      </div>
    );
  };

  const renderRenewOptions = () => {
    const renewLoading = renewReq.isLoading;
    const subLoading = currentSubReq.isLoading && !currentSub;
    const plansLoading = plansReq.isLoading && !plans.length;
    const dataReady = !!currentSub && plans.length > 0;
    const planName = currentSub?.planName || status.planName || t('manageAccount:accountStatusGate.fallbacks.yourPlan');
    const isOnProfessional =
      (currentSub?.planType || '').toLowerCase() === 'professional' ||
      (currentSub?.planName || status.planName || '').toLowerCase() ===
      'professional';

    const proPlan = findProfessionalPlan();
    const proPricing =
      proPlan?.pricing?.find(
        (p) => p.billingPeriod === currentSub?.billingPeriod
      ) || proPlan?.pricing?.[0];

    const showRecommended = !isOnProfessional && !!proPlan;

    const proPreviewLoading = proPreviewReq.isLoading && !proPreview;
    const currentPreviewLoading =
      currentPreviewReq.isLoading && !currentPreview;

    const recommendedCard = showRecommended && proPlan && (
      <div className={`relative rounded-2xl overflow-hidden border-2 border-primaryColor p-5 pt-7 shadow-sm bg-gradient-to-br ${isDark ? 'from-primaryColor/10 via-blue-900/15 to-indigo-900/15' : 'from-primaryColor/5 via-blue-50 to-indigo-50'}`}>
        <div className="absolute top-0 right-0 bg-primaryColor text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-bl-xl shadow">
          {t('manageAccount:accountStatusGate.renew.recommendedBadge')}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-5 mb-4">
          <div className="min-w-0">
            <div className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-[#101828]'}`}>
              {proPlan.planName}
            </div>
            <div className={`text-sm leading-relaxed ${isDark ? 'text-[#CBD5E1]' : 'text-[#475467]'}`}>
              {proPlan.description ||
                t('manageAccount:accountStatusGate.renew.proFallbackDesc')}
            </div>
            {proPlan.features?.length ? (
              <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 mt-3 max-h-[100px] overflow-y-auto custom-scrollbar pr-1">
                {proPlan.features.map((f) => (
                  <li
                    key={f}
                    className={`flex items-start gap-1.5 text-xs ${isDark ? 'text-[#CBD5E1]' : 'text-[#344054]'}`}
                  >
                    <CheckCircleFilled className="text-primaryColor mt-0.5 text-[11px] flex-shrink-0" />
                    <span className="leading-snug">{f}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <div className="md:border-l md:border-primaryColor/20 md:pl-5 shrink-0 min-w-[200px]">
            {/* Per-user price */}
            {proPricing ? (
              <div className="pb-3 border-b border-primaryColor/10 mb-3">
                <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                  {t('manageAccount:accountStatusGate.renew.perUser')}
                </div>
                <div className="flex items-baseline gap-1">
                  <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-[#101828]'}`}>
                    {proPricing.currencySymbol}
                    {proPricing.effectivePricePerUser.toFixed(2)}
                  </span>
                  <span className={`text-[10px] ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>{t('manageAccount:accountStatusGate.renew.perMo')}</span>
                </div>
                {proPricing.isDiscountActive &&
                  proPricing.discountPercent > 0 && (
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className={`text-[10px] line-through ${isDark ? 'text-[#64748B]' : 'text-gray-400'}`}>
                        {proPricing.currencySymbol}
                        {proPricing.pricePerUser.toFixed(2)}
                      </span>
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${isDark ? 'text-emerald-300 bg-emerald-900/30' : 'text-emerald-700 bg-emerald-50'}`}>
                        {t('manageAccount:accountStatusGate.renew.saveDiscount', { percent: proPricing.discountPercent })}
                      </span>
                    </div>
                  )}
              </div>
            ) : null}

            {/* Calculated bill */}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-primaryColor font-bold mb-1">
                {t('manageAccount:accountStatusGate.renew.billToday')}
              </div>
              {proPreviewLoading ? (
                <Skeleton
                  active
                  paragraph={{ rows: 1 }}
                  title={{ width: 100 }}
                />
              ) : proPreview ? (
                <>
                  <div className={`text-3xl font-bold leading-tight ${isDark ? 'text-white' : 'text-[#101828]'}`}>
                    {formatPrice(proPreview.amount)}
                  </div>
                  {proPreview.billType === 'ProRated' &&
                    proPreview.proRateDays &&
                    proPreview.totalDaysInPeriod ? (
                    <div className={`text-[11px] mt-1 leading-snug ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                      {t('manageAccount:accountStatusGate.renew.proRated', { days: proPreview.proRateDays, total: proPreview.totalDaysInPeriod })}
                    </div>
                  ) : proPreview.description ? (
                    <div className={`text-[11px] mt-1 leading-snug ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                      {proPreview.description}
                    </div>
                  ) : null}
                </>
              ) : (
                <div className={`text-[11px] ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                  {t('manageAccount:accountStatusGate.renew.calculating')}
                </div>
              )}
            </div>
          </div>
        </div>

        <Button
          block
          size="large"
          type="primary"
          className="bg-primaryColor !h-11 !text-sm !font-semibold"
          loading={renewLoading}
          disabled={!dataReady || renewLoading}
          onClick={handleSelectRecommendedPlan}
        >
          {renewLoading
            ? t('manageAccount:accountStatusGate.buttons.creatingBill')
            : proPreview
              ? t('manageAccount:accountStatusGate.buttons.switchToPay', { planName: proPlan.planName, amount: formatPrice(proPreview.amount) })
              : t('manageAccount:accountStatusGate.buttons.switchToWithPeriod', { planName: proPlan.planName, period: currentSub?.billingPeriod || 'Monthly' })}
        </Button>
      </div>
    );

    return (
      <div className="space-y-3">
        {recommendedCard}

        <div
          className={`grid gap-3 ${showRecommended ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'
            }`}
        >
          {/* Stay on current plan */}
          <div
            className={`rounded-xl p-4 flex flex-col ${isOnProfessional
              ? isDark
                ? 'border-2 border-primaryColor/40 bg-gradient-to-br from-primaryColor/10 to-blue-900/15'
                : 'border-2 border-primaryColor/40 bg-gradient-to-br from-primaryColor/5 to-blue-50'
              : isDark
                ? 'border border-[#2E3138] bg-[#0B172B]'
                : 'border border-[#EAECF0] bg-white'
              }`}
          >
            <div className="mb-3 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                {isOnProfessional && (
                  <span className={`text-[10px] font-bold uppercase tracking-wider text-primaryColor px-2 py-0.5 rounded-full border border-primaryColor/30 ${isDark ? 'bg-[#0B172B]' : 'bg-white'}`}>
                    {t('manageAccount:accountStatusGate.renew.recommendedTag')}
                  </span>
                )}
                <span className="text-sm font-semibold text-primaryColor">
                  {t('manageAccount:accountStatusGate.renew.keepPlan', { planName })}
                </span>
              </div>
              {subLoading ? (
                <Skeleton
                  active
                  paragraph={{ rows: 1 }}
                  title={false}
                  className="!mt-1"
                />
              ) : currentSub ? (
                <div className={`text-xs mb-3 ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                  {Number.isFinite(currentSub.effectivePrice)
                    ? t('manageAccount:accountStatusGate.renew.slotsBillingPrice', {
                      slots: t('manageAccount:accountStatusGate.renew.slots', { count: currentSub.employeeSlots }),
                      period: currentSub.billingPeriod,
                      price: currentSub.effectivePrice.toFixed(2),
                    })
                    : t('manageAccount:accountStatusGate.renew.slotsBilling', {
                      slots: t('manageAccount:accountStatusGate.renew.slots', { count: currentSub.employeeSlots }),
                      period: currentSub.billingPeriod,
                    })}
                </div>
              ) : null}

              {/* Bill preview */}
              <div className={`pt-3 border-t ${isDark ? 'border-[#2E3138]' : 'border-[#F2F4F7]'}`}>
                <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                  {t('manageAccount:accountStatusGate.renew.billToday')}
                </div>
                {currentPreviewLoading ? (
                  <Skeleton
                    active
                    paragraph={{ rows: 1 }}
                    title={{ width: 80 }}
                  />
                ) : currentPreview ? (
                  <>
                    <div className={`text-2xl font-bold leading-tight ${isDark ? 'text-white' : 'text-[#101828]'}`}>
                      {formatPrice(currentPreview.amount)}
                    </div>
                    {currentPreview.billType === 'ProRated' &&
                      currentPreview.proRateDays &&
                      currentPreview.totalDaysInPeriod ? (
                      <div className={`text-[11px] mt-1 leading-snug ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                        {t('manageAccount:accountStatusGate.renew.proRated', { days: currentPreview.proRateDays, total: currentPreview.totalDaysInPeriod })}
                      </div>
                    ) : currentPreview.description ? (
                      <div className={`text-[11px] mt-1 leading-snug line-clamp-2 ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                        {currentPreview.description}
                      </div>
                    ) : null}
                  </>
                ) : currentSub &&
                  Number.isFinite(currentSub.totalMonthlyAmount) ? (
                  <div className={`text-2xl font-bold leading-tight ${isDark ? 'text-white' : 'text-[#101828]'}`}>
                    ${currentSub.totalMonthlyAmount.toFixed(2)}
                  </div>
                ) : (
                  <div className={`text-[11px] ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>{t('manageAccount:accountStatusGate.renew.calculating')}</div>
                )}
              </div>
            </div>
            <Button
              block
              type={isOnProfessional ? 'primary' : 'default'}
              className={isOnProfessional ? 'bg-primaryColor' : ''}
              loading={renewLoading && !showRecommended}
              disabled={!dataReady || renewLoading}
              onClick={handleRenewCurrentPlan}
            >
              {plansLoading || subLoading
                ? t('manageAccount:accountStatusGate.buttons.loading')
                : currentPreview && !isOnProfessional
                  ? t('manageAccount:accountStatusGate.buttons.renewPay', { amount: formatPrice(currentPreview.amount) })
                  : t('manageAccount:accountStatusGate.buttons.renewWithPlan', { planName })}
            </Button>
          </div>

          {/* Switch / Browse all */}
          <div className={`rounded-xl border p-4 flex flex-col ${isDark ? 'border-[#2E3138] bg-[#0B172B]' : 'border-[#EAECF0] bg-white'}`}>
            <div className="flex-1 mb-3">
              <div className="text-sm font-semibold text-primaryColor mb-1">
                {t('manageAccount:accountStatusGate.renew.compareTitle')}
              </div>
              <div className={`text-xs leading-snug ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                {t('manageAccount:accountStatusGate.renew.compareSubtitle')}
              </div>
            </div>
            <Button block onClick={enterPicker}>
              {t('manageAccount:accountStatusGate.buttons.browsePlans')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderBody = () => {
    if (view === 'picker') return renderPicker();
    switch (mode.kind) {
      case 'trial-reminder':
        return (
          <div>
            <Hero
              icon={<InfoCircleFilled />}
              tone="blue"
              title={t('manageAccount:accountStatusGate.heroes.trialReminderTitle')}
              subtitle={status.message}
            />
            <div className={`bg-gradient-to-br rounded-xl border p-4 ${isDark ? 'from-blue-900/15 to-indigo-900/15 border-blue-900/40' : 'from-blue-50 to-indigo-50 border-blue-100'}`}>
              <StatRow
                label={t('manageAccount:accountStatusGate.stats.daysRemaining')}
                value={
                  <span className="text-lg">
                    {status.trialDaysRemaining ?? '—'}
                  </span>
                }
              />
              {status.trialEndDate && (
                <div className={`pt-2 mt-2 border-t ${isDark ? 'border-blue-900/40' : 'border-blue-100'}`}>
                  <StatRow
                    label={t('manageAccount:accountStatusGate.stats.trialEnds')}
                    value={formatDate(status.trialEndDate)}
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 'trial-expired':
        return (
          <div>
            <Hero
              icon={<ExclamationCircleFilled />}
              tone="amber"
              title={t('manageAccount:accountStatusGate.heroes.trialExpiredTitle')}
              subtitle={
                hasUnpaidBills
                  ? t('manageAccount:accountStatusGate.heroes.payOrCancelSubtitle')
                  : status.message ||
                  t('manageAccount:accountStatusGate.heroes.trialExpiredFallback')
              }
            />
            {hasUnpaidBills ? (
              renderUnpaidBillsList()
            ) : (
              <div className={`bg-gradient-to-br rounded-xl border p-4 text-sm ${isDark ? 'from-amber-900/15 to-orange-900/15 border-amber-900/40 text-amber-200' : 'from-amber-50 to-orange-50 border-amber-100 text-amber-900'}`}>
                {t('manageAccount:accountStatusGate.info.pickPlanNote')}
              </div>
            )}
          </div>
        );

      case 'bill-due':
        return (
          <div>
            <Hero
              icon={<CreditCardOutlined />}
              tone="blue"
              title={t('manageAccount:accountStatusGate.heroes.billDueTitle')}
              subtitle={status.message}
            />
            {hasUnpaidBills ? (
              renderUnpaidBillsList()
            ) : (
              <div className={`bg-gradient-to-br rounded-xl border p-4 ${isDark ? 'from-blue-900/15 to-indigo-900/15 border-blue-900/40' : 'from-blue-50 to-indigo-50 border-blue-100'}`}>
                <div className="flex items-baseline justify-between">
                  <span className={`text-xs uppercase tracking-wider font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                    {t('manageAccount:accountStatusGate.stats.amountDue')}
                  </span>
                  <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-[#101828]'}`}>
                    {formatPrice(status.dueBillAmount)}
                  </span>
                </div>
                {status.billDueDate && (
                  <div className={`pt-2 mt-2 border-t ${isDark ? 'border-blue-900/40' : 'border-blue-100'}`}>
                    <StatRow
                      label={t('manageAccount:accountStatusGate.stats.dueBy')}
                      value={formatDate(status.billDueDate)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 'grace-ongoing':
        return (
          <div>
            <Hero
              icon={<ClockCircleOutlined />}
              tone="amber"
              title={
                status.planName && !hasUnpaidBills
                  ? t('manageAccount:accountStatusGate.titles.gracePlanEnding', { planName: status.planName })
                  : t('manageAccount:accountStatusGate.titles.graceTitleFallback')
              }
              subtitle={status.message}
            />
            {hasUnpaidBills ? (
              renderUnpaidBillsList()
            ) : (
              <>
                {
                  !status.isAdminAssigned && (
                    <>
                      <div className={`bg-gradient-to-br rounded-xl border p-4 mb-3 space-y-2 ${isDark ? 'from-amber-900/15 to-orange-900/15 border-amber-900/40' : 'from-amber-50 to-orange-50 border-amber-100'}`}>
                        <StatRow
                          label={t('manageAccount:accountStatusGate.stats.graceEnds')}
                          value={formatDate(status.graceEndDate)}
                        />
                        {status.graceHoursRemaining > 0 && (
                          <StatRow
                            label={t('manageAccount:accountStatusGate.stats.timeRemaining')}
                            value={t('manageAccount:accountStatusGate.stats.hoursSuffix', { hours: Math.ceil(status.graceHoursRemaining) })}
                            valueClassName={isDark ? 'text-amber-300' : 'text-amber-700'}
                          />
                        )}
                      </div>
                      {renderRenewOptions()}
                    </>
                  )
                }
              </>
            )}
          </div>
        );

      case 'grace-expired':
        return (
          <div>
            <Hero
              icon={<ExclamationCircleFilled />}
              tone="red"
              title={
                status.planName && !hasUnpaidBills
                  ? t('manageAccount:accountStatusGate.titles.gracePlanEnded', { planName: status.planName })
                  : t('manageAccount:accountStatusGate.titles.graceEndedFallback')
              }
              subtitle={
                hasUnpaidBills
                  ? t('manageAccount:accountStatusGate.heroes.payOrCancelSubtitle')
                  : status.message || t('manageAccount:accountStatusGate.heroes.graceEndedFallback')
              }
            />
            {hasUnpaidBills ? (
              renderUnpaidBillsList()
            ) : <>
              {
                !status.isAdminAssigned && (
                  renderRenewOptions()
                )
              }
            </>}
          </div>
        );

      case 'suspended':
        return (
          <div>
            <Hero
              icon={<StopOutlined />}
              tone="red"
              title={t('manageAccount:accountStatusGate.heroes.suspendedTitle')}
              subtitle={t('manageAccount:accountStatusGate.heroes.suspendedSubtitle')}
            />
            <div className={`bg-gradient-to-br rounded-xl border p-4 text-sm leading-relaxed ${isDark ? 'from-red-900/15 to-rose-900/15 border-red-900/40 text-red-200' : 'from-red-50 to-rose-50 border-red-100 text-red-900'}`}>
              {t('manageAccount:accountStatusGate.info.suspendedHelp')}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderFooter = () => {
    // Blocking modals have no header close (X), no mask/keyboard dismiss,
    // and no "remind me later" — give the user a way out via logout.
    const logoutBtn = blocking ? (
      <Button icon={<LogoutOutlined />} onClick={handleLogout}>
        {t('manageAccount:accountStatusGate.buttons.logout')}
      </Button>
    ) : null;

    if (view === 'picker') {
      // Back is always available — the gate's main view (Hero + body) never
      // renders empty, so returning to it is always safe.
      return (
        <>
          {logoutBtn}
          <Button onClick={exitPicker} icon={<ArrowLeftOutlined />}>
            {t('manageAccount:accountStatusGate.buttons.back')}
          </Button>
        </>
      );
    }

    const dismissBtn = blocking ? null : (
      <Button onClick={handleDismiss}>{t('manageAccount:accountStatusGate.buttons.remindMeLater')}</Button>
    );

    switch (mode.kind) {
      case 'trial-reminder':
        return (
          <>
            {dismissBtn}
            <Button
              type="primary"
              className="bg-primaryColor"
              onClick={enterPicker}
            >
              {t('manageAccount:accountStatusGate.buttons.chooseAPlan')}
            </Button>
          </>
        );
      case 'trial-expired':
        // Blocking — show plan picker CTA only when there are no unpaid bills
        // to resolve first. With bills, Pay/Cancel are inline per bill.
        return (
          <>
            {logoutBtn}
            {!hasUnpaidBills && (
              <Button
                type="primary"
                className="bg-primaryColor"
                onClick={enterPicker}
              >
                {t('manageAccount:accountStatusGate.buttons.chooseAPlan')}
              </Button>
            )}
          </>
        );
      case 'grace-expired':
        // Body shows Pay/Cancel per bill OR renew/switch CTAs — footer only
        // carries the logout escape hatch since this state is blocking.
        return logoutBtn;
      case 'bill-due':
         // No footer — body shows Pay/Cancel per bill OR renew/switch CTAs.
        return null;
      case 'grace-ongoing':
        // Body already has renew/switch CTAs when no unpaid bills.
        // With unpaid bills, fall back to Pay-now footer.
        return (
          dismissBtn
        );
      case 'suspended':
        return (
          <>
            {logoutBtn}
            <Button
              type="primary"
              className="bg-primaryColor"
              onClick={() =>
                window.open('https://trackforce.io/contact/', '_blank')
              }
            >
              {t('manageAccount:accountStatusGate.buttons.contactSupport')}
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  // Mirrors the body's own condition for rendering renderRenewOptions()
  // (see the grace-ongoing/grace-expired cases in renderBody): a plan name
  // isn't required, only that there are no unpaid bills to resolve first
  // and the account isn't admin-assigned (which has no self-serve renewal).
  const showsRenewOptions =
    (mode.kind === 'grace-ongoing' || mode.kind === 'grace-expired') &&
    !hasUnpaidBills &&
    !status.isAdminAssigned;

  const title =
    view === 'picker'
      ? t('manageAccount:accountStatusGate.titles.pickAPlan')
      : mode.kind === 'trial-reminder'
        ? t('manageAccount:accountStatusGate.titles.trialReminder', { count: status.trialDaysRemaining ?? 0 })
        : mode.kind === 'trial-expired'
          ? t('manageAccount:accountStatusGate.titles.trialEnded')
          : mode.kind === 'bill-due'
            ? t('manageAccount:accountStatusGate.titles.outstandingBill')
            : mode.kind === 'grace-ongoing'
              ? showsRenewOptions
                ? t('manageAccount:accountStatusGate.titles.renewSubscription')
                : t('manageAccount:accountStatusGate.titles.gracePaymentDue')
              : mode.kind === 'grace-expired'
                ? showsRenewOptions
                  ? t('manageAccount:accountStatusGate.titles.renewSubscription')
                  : t('manageAccount:accountStatusGate.titles.graceEnded')
                : t('manageAccount:accountStatusGate.titles.suspended');

  return (
    <>
      <Modal
        open={open && !payOpen && !selectPlanOpen}
        title={
          <div className={`text-base font-semibold ${isDark ? 'text-white' : 'text-[#101828]'}`}>{title}</div>
        }
        footer={
          <div className="flex justify-end gap-2 pt-1">{renderFooter()}</div>
        }
        closable={!blocking}
        maskClosable={!blocking}
        keyboard={!blocking}
        onCancel={handleDismiss}
        destroyOnClose
        centered
        width={(() => {
          if (view === 'picker') return 1080;
          return showsRenewOptions ? 920 : 560;
        })()}
        zIndex={1100}
        styles={{
          header: {
            borderBottom: `1px solid ${isDark ? '#2E3138' : '#F2F4F7'}`,
            paddingBottom: 12,
            marginBottom: 16,
          },
          footer: {
            borderTop: `1px solid ${isDark ? '#2E3138' : '#F2F4F7'}`,
            paddingTop: 12,
            marginTop: 16,
          },
        }}
      >
        {renderBody()}
      </Modal>

      {payTarget && (
        <BillingModal
          billingId={String(payTarget.billId)}
          ammount={payTarget.amount}
          open={payOpen}
          setOpen={handlePayClose}
        />
      )}

      <SelectPlanModal
        open={selectPlanOpen}
        onClose={handleSelectPlanClose}
        onCompleted={handleSelectPlanCompleted}
        plan={selectedPlan}
        billingPeriod={billingPeriod}
        currentEmployeeCount={currentSub?.employeesUsed ?? 0}
      />
    </>
  );
};

export default AccountStatusGate;
